/* eslint-disable no-shadow */
/* eslint-disable react/display-name */
import BusinessIcon from "@material-ui/icons/Business";
import HomeIcon from "@material-ui/icons/Home";
import ListIcon from "@material-ui/icons/List";
import PersonIcon from "@material-ui/icons/Person";
import PlaceIcon from "@material-ui/icons/Place";
import React, { FC, useState } from "react";
import { useDispatch } from "react-redux";

import BaseStepper from "../../components/modules/BaseStepper";
// import CRMRetargetingModal from "../../components/Advertisers/CRMRetargetingModal";
import Controls from "../../components/CampaignCreation/Controls";
import CustomSlideCheckBoxOption from "../../components/CampaignCreation/CustomSlideCheckBoxOption";
import PageLayout from "../../layouts/PageLayout";
import classes from "./CustomCampainCreatePage.module.scss";
import { BaseStep } from "../../components/modules/BaseStepper/BaseStepper";
import {
  PublicationListSelectionStep,
  RoleTitleSelectionStep,
  StateProvinceSelectionStep,
  TargetCompaniesSelectionStep,
  VisitorsRetargetingSegmentsSelectionStep,
  ZipCodeSelectionStep,
  steps as defaultSteps,
} from "../../constants/steps";
import { createCampaign, resetCampaignToCreateState, setCampaignSteps } from "../../redux/reducers/campaignToCreate";

const creationStartedPageLayoutProps = {
  heading: "Ad Campaign Creation",
  description: "",
};

const customSlidesSelectionPageLayoutProps = {
  heading: "Select all relevant types of targeting you want to include in this campaign.",
  description:
    "Please note, additional targeting layers will reduce your overall available audience and may impact available impressions.",
};

const CustomCampainCreatePage: FC = () => {
  const [customSlideOptions, setCustomSlideOptions] = useState({
    stateProvinces: false,
    zipCodes: false,
    publicationLists: false,
    companyIps: false,
    roleTitles: false,
    visitorsRetargetingSegments: false,
    // crmRetargetingSegments: false,
  });

  const [steps, setSteps] = useState<BaseStep[]>(defaultSteps);

  const [hasStartedCustomCampaignCreation, setHasStartedCustomCampaignCreation] = useState(false);

  const handleCustomSlideOptionsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { checked, name } = event.target;

    setCustomSlideOptions({
      ...customSlideOptions,
      [name]: checked,
    });

    const toggleStep = (checked: boolean, stepComponent: BaseStep, label: string) => {
      if (checked) {
        setSteps([...steps, stepComponent]);
      } else {
        const stepIndex = steps.findIndex((step) => step.label === label);
        setSteps(steps.filter((_, index: number) => stepIndex !== index));
      }
    };

    switch (name) {
      case "roleTitles": {
        toggleStep(checked, RoleTitleSelectionStep, "Role/Title");
        break;
      }

      case "companyIps": {
        toggleStep(checked, TargetCompaniesSelectionStep, "Target Companies");
        break;
      }

      case "publicationLists": {
        toggleStep(checked, PublicationListSelectionStep, "Publication Lists");
        break;
      }

      case "zipCodes": {
        toggleStep(checked, ZipCodeSelectionStep, "Zip Codes");
        break;
      }

      case "stateProvinces": {
        toggleStep(checked, StateProvinceSelectionStep, "State/Province");
        break;
      }

      case "visitorsRetargetingSegments": {
        toggleStep(checked, VisitorsRetargetingSegmentsSelectionStep, "Visitors Re-targeting Segments");
        break;
      }

      // CRM Re-targeting Segments disabled for now
      // case "crmRetargetingSegments": {
      //   toggleStep(checked, CRMRetargetingSegmentsSelectionStep, "CRM Re-targeting Segments");
      //   break;
      // }

      default:
        break;
    }
  };

  const dispatch = useDispatch();

  const handleGetStarted = () => {
    const isTargetCompanyStepIncluded = steps.find((step) => step.label === "Target Companies");
    if (isTargetCompanyStepIncluded) {
      localStorage.setItem("customCampaignType", "companyIps");
    } else {
      localStorage.removeItem("customCampaignType");
    }

    dispatch(setCampaignSteps(steps.map((step) => step.name)));
    setHasStartedCustomCampaignCreation(true);
  };

  const handleBack = () => {
    dispatch(resetCampaignToCreateState());
    setHasStartedCustomCampaignCreation(false);
  };

  return (
    <PageLayout
      heading={
        hasStartedCustomCampaignCreation
          ? creationStartedPageLayoutProps.heading
          : customSlidesSelectionPageLayoutProps.heading
      }
      description={
        hasStartedCustomCampaignCreation
          ? creationStartedPageLayoutProps.description
          : customSlidesSelectionPageLayoutProps.description
      }
    >
      {hasStartedCustomCampaignCreation ? (
        <BaseStepper steps={steps} handleBack={handleBack} lastStepNextLabel="Create" campaignAction={createCampaign} />
      ) : (
        <>
          <div className={classes.customSlideOptionsContainer}>
            <CustomSlideCheckBoxOption
              label="Geography (State / Province)"
              description="Target audiences using U.S. State and Canada Provinces."
              icon={<PlaceIcon />}
              checked={customSlideOptions.stateProvinces}
              name="stateProvinces"
              handleChange={handleCustomSlideOptionsChange}
            />

            <CustomSlideCheckBoxOption
              label="Geography (ZIP Code)"
              description="Target audiences using U.S. Zip Codes."
              icon={<HomeIcon />}
              checked={customSlideOptions.zipCodes}
              name="zipCodes"
              handleChange={handleCustomSlideOptionsChange}
            />
            <CustomSlideCheckBoxOption
              label="Publication Lists"
              description="Target audiences using BrandGen verified publications or upload your own publication list."
              icon={<ListIcon />}
              checked={customSlideOptions.publicationLists}
              name="publicationLists"
              handleChange={handleCustomSlideOptionsChange}
            />
            <CustomSlideCheckBoxOption
              label="Company Domain"
              description="Target Account-Based audiences using company domains."
              icon={<BusinessIcon />}
              checked={customSlideOptions.companyIps}
              name="companyIps"
              handleChange={handleCustomSlideOptionsChange}
            />
            <CustomSlideCheckBoxOption
              label="Role / Title"
              description="Target audiences using BrandGen curated Job Role and Title categories."
              icon={<PersonIcon />}
              checked={customSlideOptions.roleTitles}
              name="roleTitles"
              handleChange={handleCustomSlideOptionsChange}
            />

            <CustomSlideCheckBoxOption
              label="Visitors Re-targeting Segments"
              description="Target audiences using BrandGen segments that you have previously targeted."
              icon={<PersonIcon />}
              checked={customSlideOptions.visitorsRetargetingSegments}
              name="visitorsRetargetingSegments"
              handleChange={handleCustomSlideOptionsChange}
            />

            {/* CRM Re-targeting Segments disabled for now
            <CustomSlideCheckBoxOption
              label="CRM Re-targeting Segments"
              description="Target audiences using CRM Re-targeting Segments."
              icon={<PersonIcon />}
              checked={customSlideOptions.crmRetargetingSegments}
              name="crmRetargetingSegments"
              handleChange={handleCustomSlideOptionsChange}
            />
            */}
          </div>
          <Controls handleGetStarted={handleGetStarted} />
          {/* <CRMRetargetingModal triggerCondition={customSlideOptions?.crmRetargetingSegments} /> */}
        </>
      )}
    </PageLayout>
  );
};

export default CustomCampainCreatePage;
